import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { ARTICLES } from "@/lib/articles";
import { usePageTitle } from "@/lib/site";

const Block = ({ block }) => {
  if (block.t === "h2") {
    return (
      <h2 className="pt-6 font-serif text-3xl tracking-tight text-ink">{block.text}</h2>
    );
  }
  if (block.t === "ul") {
    return (
      <ul className="space-y-3.5">
        {block.items.map((item) => (
          <li key={item.slice(0, 40)} className="flex items-start gap-3 text-base leading-relaxed text-zinc-700 sm:text-lg">
            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-clay" />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  return <p className="text-base leading-relaxed text-zinc-700 sm:text-lg">{block.text}</p>;
};

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  usePageTitle(article ? `${article.title} | CalCor Solutions` : "Article not found | CalCor Solutions");

  useEffect(() => {
    if (!article) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "article-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      author: { "@type": "Organization", name: "CalCor Solutions", url: "https://calcorsolutions.com" },
      publisher: { "@type": "Organization", name: "CalCor Solutions", url: "https://calcorsolutions.com" },
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [article]);

  if (!article) {
    return (
      <section data-testid="article-not-found" className="flex min-h-[70vh] flex-col items-start justify-center px-6 pt-36 lg:px-10">
        <h1 className="font-serif text-4xl tracking-tighter text-ink">That article slipped off the ledger.</h1>
        <Link
          to="/insights"
          data-testid="article-back-to-insights"
          className="group mt-8 inline-flex items-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
        >
          <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
          Back to insights
        </Link>
      </section>
    );
  }

  const others = ARTICLES.filter((a) => a.slug !== slug);

  return (
    <>
      <article data-testid="article-page" className="pt-36 pb-24 sm:pt-44 lg:pb-32">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <FadeUp>
            <Link
              to="/insights"
              data-testid="article-back-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-pine transition-colors hover:text-clay"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              All insights
            </Link>
          </FadeUp>

          <p className="mt-10 text-xs font-semibold uppercase tracking-[0.2em] text-clay">
            {article.category} · {article.date} · {article.readTime}
          </p>
          <h1 className="mt-5 font-serif text-4xl font-medium leading-[1.12] tracking-tighter text-ink sm:text-5xl">
            <MaskedLine delay={0.15}>{article.title}</MaskedLine>
          </h1>

          <FadeUp delay={0.25}>
            <div className="mt-12 space-y-7">
              {article.body.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </div>
          </FadeUp>

          <FadeUp>
            <div className="mt-16 rounded-3xl border border-black/10 bg-white p-9 sm:p-10">
              <h2 className="font-serif text-2xl tracking-tight text-ink">
                Questions this raised?
              </h2>
              <p className="mt-3 max-w-lg text-sm leading-relaxed text-zinc-600 sm:text-base">
                The first conversation with CalCor is free — thirty minutes, plain English, no
                obligation. You will leave knowing your clear next step.
              </p>
              <Link
                to="/contact"
                data-testid="article-cta-button"
                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-pine px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
              >
                Request a consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeUp>

          <div className="mt-16">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-pine">Keep reading</p>
            <div className="mt-6 space-y-0">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/insights/${o.slug}`}
                  data-testid={`article-related-${o.slug}`}
                  className="group flex items-center justify-between gap-6 border-t border-black/10 py-6 last:border-b"
                >
                  <span className="font-serif text-lg tracking-tight text-ink transition-colors duration-300 group-hover:text-pine sm:text-xl">
                    {o.title}
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-zinc-400 transition-all duration-300 group-hover:translate-x-1 group-hover:text-pine" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
    </>
  );
}
